import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Basketball, Users, Clock, MapPin, Trophy, Heart, CheckCircle, CreditCard, Envelope, InstagramLogo, ArrowRight, Lightning, Target, ShieldCheck, Calendar, Handshake, Camera, X } from '@phosphor-icons/react';
import { getApiBaseUrl } from '../utils/api';

// Helper to format time to 12-hour
const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return timeString;
  }
};

// Hero background images - Real STAY ON session photos
const heroImages = [
  '/hero2.webp', // Team huddle with NO EGOS jerseys
  '/hero4.webp', // Group photo
];

export function Landing() {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [sessionPhotos, setSessionPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);
  
  // Fetch upcoming sessions (public endpoint)
  const fetchSessions = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/public`);
      if (response.ok) {
        const data = await response.json();
        // Get today's date in local timezone
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        
        // Filter to only upcoming sessions and limit to 4
        const upcoming = data
          .filter(s => s.date >= today && s.published !== false)
          .sort((a, b) => {
            if (a.date !== b.date) return a.date.localeCompare(b.date);
            return (a.start_time || '').localeCompare(b.start_time || '');
          })
          .slice(0, 4);
        
        setUpcomingSessions(upcoming);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  };
  
  // Fetch recent session photos (public endpoint)
  const fetchPhotos = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/cloudinary/photos/public/recent?limit=500`);
      if (response.ok) {
        const data = await response.json();
        setSessionPhotos(data.sessions || []);
      }
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchSessions();
    fetchPhotos();
  }, []);
  
  // Auto-refresh every 30 seconds for real-time spot updates
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchSessions();
    }, 30000); // 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Premium Navigation */}
      <nav className="fixed top-0 w-full glass-effect z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
            alt="STAY ON Logo" 
            className="h-10 md:h-14"
            data-testid="nav-logo"
          />
          <div className="flex items-center gap-3 md:gap-6">
            <Button 
              onClick={() => navigate('/login')}
              variant="ghost"
              className="text-white/80 hover:text-white hover:bg-white/10 font-medium"
            >
              Sign In
            </Button>
            <Button 
              onClick={() => navigate('/onboarding')}
              className="btn-primary px-6 md:px-8"
              data-testid="nav-register-btn"
            >
              Get Started
              <ArrowRight size={18} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO SECTION - Cinematic Full Screen */}
      <section className="relative min-h-screen flex items-center justify-center">
        {/* Rotating Background */}
        {heroImages.map((image, index) => (
          <div
            key={index}
            className="absolute inset-0 transition-opacity duration-1000"
            style={{
              backgroundImage: `url('${image}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              opacity: index === currentImageIndex ? 1 : 0,
            }}
          />
        ))}
        
        {/* Gradient Overlay - Lighter to show photos */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-background" />
        
        {/* Content */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-24 pb-16 text-center">
          <div className="fade-in-up">
            {/* Overline */}
            <p className="text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-primary mb-6">
              Youth Basketball Development
            </p>
            
            {/* Main Headline */}
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-8xl font-black uppercase tracking-tight leading-[0.95] mb-8 px-2 sm:px-4 overflow-visible">
              <span className="text-white block">Win & Stay On</span>
              <span className="text-gradient block pr-2">The Game Decides</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              Organized pickup basketball for grades 3-12. Real game play. Real competition. 
              No sideline coaching. Just basketball the way it should be played.
            </p>
            
            {/* CTA Group */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button 
                onClick={() => navigate('/onboarding')}
                className="btn-primary text-lg px-10 py-6 w-full sm:w-auto"
                data-testid="hero-register-btn"
              >
                <Users size={22} weight="bold" className="mr-3" />
                Get Started
              </Button>
              <Button 
                onClick={() => {
                  const sessionsSection = document.getElementById('upcoming-sessions');
                  if (sessionsSection) {
                    sessionsSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                variant="outline"
                className="text-white border-white/30 hover:bg-white/10 hover:border-white/50 px-8 py-6 w-full sm:w-auto"
              >
                View Sessions
              </Button>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-primary" />
                <span>Grades 3-12</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-primary" />
                <span>All Skill Levels</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle size={20} weight="fill" className="text-primary" />
                <span>Pay Per Session</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImageIndex(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentImageIndex ? 'bg-primary w-8' : 'bg-white/30 w-4 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO GRID */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight">
              The Missing Piece
            </h2>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard 
              icon={<Basketball size={32} weight="duotone" />}
              title="Organized Pickup"
              description="Non-instructional game play emphasizing decision-making and real competition. No whistles, no plays—just ball."
              className="lg:col-span-2"
              large
            />
            
            <FeatureCard 
              icon={<ShieldCheck size={32} weight="duotone" />}
              title="Adult Supervised"
              description="Safe environment with general adult supervision. Players learn to self-regulate."
            />
            
            <FeatureCard 
              icon={<Target size={32} weight="duotone" />}
              title="Natural Development"
              description="Skills develop through real game situations, not drills. Basketball IQ that can't be taught."
            />
            
            <FeatureCard 
              icon={<Lightning size={32} weight="duotone" />}
              title="Own Pace"
              description="Players participate at their comfort level. Challenge accepted when they're ready."
              className="lg:col-span-2"
              large
            />
          </div>
        </div>
      </section>

      {/* PROGRAM DETAILS - Split Section */}
      <section className="py-20 md:py-32 px-6 bg-card/30">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image Side */}
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden">
                <img 
                  src="/hero4.webp"
                  alt="STAY ON Basketball Players"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-6 -right-6 bg-card border border-border rounded-xl p-6 shadow-2xl">
                <p className="text-4xl font-black text-primary">3-12</p>
                <p className="text-sm text-muted-foreground uppercase tracking-wide">Grade Levels</p>
              </div>
            </div>
            
            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Program Details</p>
              <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-8">
                Built For Ballers
              </h2>
              
              <div className="space-y-6">
                <DetailRow 
                  icon={<Users size={24} weight="duotone" />}
                  title="Grade Divisions"
                  text="Elementary (3-5), Middle (6-8), High School (9-12)"
                />
                <DetailRow 
                  icon={<Clock size={24} weight="duotone" />}
                  title="Flexible Schedule"
                  text="Multiple sessions weekly. Fit it into your family's routine."
                />
                <DetailRow 
                  icon={<MapPin size={24} weight="duotone" />}
                  title="Premium Facilities"
                  text="Modern courts, proper equipment, climate controlled."
                />
                <DetailRow 
                  icon={<CreditCard size={24} weight="duotone" />}
                  title="Simple Pricing"
                  text="Pay per session. No commitments. No hidden fees."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Get Started</p>
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-16">
            Three Steps To The Court
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <StepCard number="01" title="Create Account" description="Sign up in 2 minutes. Add your family profile." />
            <StepCard number="02" title="Add Player & Waiver" description="Add your child and sign the waiver in one flow." />
            <StepCard number="03" title="Book & Play" description="Choose sessions. Pay securely. Show up ready." />
          </div>
          
          <Button 
            onClick={() => navigate('/onboarding')}
            className="btn-primary text-lg px-12 py-6 mt-12"
            data-testid="steps-register-btn"
          >
            Get Started
            <ArrowRight size={20} weight="bold" className="ml-3" />
          </Button>
        </div>
      </section>

      {/* PHILOSOPHY SECTION */}
      <section className="py-20 md:py-32 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5" />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Our Philosophy</p>
          <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-8">
            The Bridge They Need
          </h2>
          
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            Between structured training and organized games, kids need the pickup experience. 
            They need to learn how to compete, communicate, and resolve disputes on their own. 
            This builds basketball IQ, confidence, and character that can't be taught.
          </p>
          
          <blockquote className="text-2xl md:text-3xl font-black text-primary uppercase tracking-tight">
            "Win and Stay On. Lose and Wait. The Game Decides."
          </blockquote>
        </div>
      </section>

      {/* NOT ANTI-COACH SECTION */}
      <section className="py-20 md:py-32 px-6 bg-card/30">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Handshake size={32} weight="duotone" className="text-primary" />
                </div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Important Note</p>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-6">
                We're Not Anti-Coach<br/>or Anti-Trainer
              </h2>
              
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  <strong className="text-white">We believe in coaches and trainers.</strong> They're essential for skill development, 
                  team strategy, and structured learning. What they provide is invaluable.
                </p>
                <p>
                  But there's a gap. Between practice drills and organized games, kids need unstructured play. 
                  They need to figure things out on their own, make mistakes, and learn to compete without someone 
                  calling every play.
                </p>
                <p>
                  <strong className="text-white">STAY ON fills that gap.</strong> We complement what coaches and trainers do, 
                  not replace them. Think of us as the missing piece – the real-world application of everything they're learning.
                </p>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-2xl p-8">
              <h3 className="font-bold text-lg mb-6 text-center">The Complete Development Path</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Target size={20} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Trainers</p>
                    <p className="text-sm text-muted-foreground">Skill development & technique</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Users size={20} className="text-green-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Coaches</p>
                    <p className="text-sm text-muted-foreground">Team play & strategy</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Basketball size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary">STAY ON</p>
                    <p className="text-sm text-muted-foreground">Real-game application & IQ</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING SESSIONS SECTION - Always show */}
      <section id="upcoming-sessions" className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Don't Miss Out</p>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-4">
              Upcoming Sessions
            </h2>
            <p className="text-muted-foreground">Spots are limited. Register now to secure your place.</p>
          </div>
          
          {upcomingSessions.length === 0 ? (
            /* No Sessions Message */
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar size={40} className="text-primary" />
              </div>
              <h3 className="text-2xl font-bold mb-3">No Upcoming Sessions</h3>
              <p className="text-muted-foreground max-w-md mx-auto mb-8">
                We're currently planning our next basketball sessions. Check back soon or create an account to get notified when new sessions are available!
              </p>
              <Button 
                onClick={() => navigate('/onboarding')}
                className="btn-primary px-8"
              >
                Get Notified
                <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
            </div>
          ) : (
            /* Sessions Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {upcomingSessions.map((session) => {
                const spotsLeft = session.capacity - (session.registered_count || 0);
                const sessionDate = new Date(session.date + 'T12:00:00');
                const dayName = sessionDate.toLocaleDateString('en-US', { weekday: 'short' });
                const monthDay = sessionDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const isSoldOut = spotsLeft <= 0;
                const isLowStock = spotsLeft > 0 && spotsLeft <= 3;
                const isModerateStock = spotsLeft > 3 && spotsLeft <= 5;
                
                // Check if session has ended (date is in the past)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const sessionDateOnly = new Date(session.date + 'T00:00:00');
                const isSessionEnded = sessionDateOnly < today || session.status === 'ended';
                
                return (
                  <div 
                    key={session.session_id}
                    className={`rounded-xl overflow-hidden transition-all group relative ${
                      isSessionEnded
                        ? 'bg-card/50 border-2 border-gray-500/30 opacity-75'
                        : isSoldOut 
                          ? 'bg-card/50 border-2 border-red-500/30' 
                          : isLowStock
                            ? 'bg-card border-2 border-yellow-500/50 hover:border-yellow-500 shadow-lg shadow-yellow-500/10'
                            : 'bg-card border border-border hover:border-primary/50'
                    }`}
                  >
                    {/* Session Ended Banner */}
                    {isSessionEnded && (
                      <div className="bg-gray-500 text-white text-xs font-bold text-center py-1">
                        SESSION ENDED
                      </div>
                    )}
                    
                    {/* Urgency Banner for Low Stock */}
                    {!isSessionEnded && isLowStock && !isSoldOut && (
                      <div className="bg-yellow-500 text-black text-xs font-bold text-center py-1 animate-pulse">
                        🔥 ALMOST FULL - REGISTER NOW!
                      </div>
                    )}
                    
                    {/* Session Ended Overlay */}
                    {isSessionEnded && (
                      <div className="absolute inset-0 bg-black/60 z-10 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-gray-300 font-black text-lg mb-1">SESSION ENDED</p>
                          <p className="text-gray-400 text-sm">Registration closed</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Sold Out Overlay */}
                    {!isSessionEnded && isSoldOut && (
                      <div className="absolute inset-0 bg-black/60 z-10 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-white font-black text-lg mb-1">SOLD OUT</p>
                          <p className="text-white/80 text-sm">Join Waitlist →</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Date Header */}
                    <div className={`px-4 py-3 border-b border-border ${
                      isSoldOut ? 'bg-red-500/10' : isLowStock ? 'bg-yellow-500/10' : 'bg-primary/10'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs font-bold uppercase ${
                            isSoldOut ? 'text-red-400' : isLowStock ? 'text-yellow-500' : 'text-primary'
                          }`}>{dayName}</p>
                          <p className="text-lg font-black">{monthDay}</p>
                        </div>
                        <Calendar size={24} className={
                          isSoldOut ? 'text-red-400/50' : isLowStock ? 'text-yellow-500/50' : 'text-primary/50'
                        } />
                      </div>
                    </div>
                    
                    {/* Session Info */}
                    <div className="p-4">
                      {/* Session Title */}
                      {session.title && (
                        <p className="text-sm font-bold text-primary mb-2">{session.title}</p>
                      )}
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                        <Clock size={14} />
                        <span>{formatTime(session.start_time)}</span>
                      </div>
                      
                      {session.location && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                          <MapPin size={14} />
                          <span>{session.location.name}</span>
                        </div>
                      )}
                      
                      {/* Spots Badge with Urgency */}
                      {isSoldOut ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-red-500/20 text-red-500">
                          <Users size={14} />
                          Waitlist Available
                        </div>
                      ) : isLowStock ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-yellow-500 text-black animate-pulse">
                          <Users size={14} />
                          Only {spotsLeft} spot{spotsLeft === 1 ? '' : 's'} left!
                        </div>
                      ) : isModerateStock ? (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-yellow-500/20 text-yellow-500">
                          <Users size={14} />
                          {spotsLeft} spots left
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-bold bg-green-500/20 text-green-500">
                          <Users size={14} />
                          {spotsLeft} spots available
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
            
          {upcomingSessions.length > 0 && (
            <div className="text-center mt-10">
              <Button 
                onClick={() => navigate('/onboarding')}
                className="btn-primary text-lg px-10 py-6"
              >
                Register Now
                <ArrowRight size={20} weight="bold" className="ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground mt-4">
                Create an account to see all available sessions
              </p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ GRID */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">FAQ</p>
            <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight">
              Common Questions
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FAQCard question="What should my child bring?" answer="Water bottle, athletic shoes, comfortable clothes. We provide basketballs." />
            <FAQCard question="Why no sideline coaching?" answer="Kids develop independence, make their own decisions, and learn to resolve conflicts naturally." />
            <FAQCard question="Is this a coaching program?" answer="No. Organized pickup basketball with adult supervision for safety only." />
            <FAQCard question="How physical is play?" answer="Physical play is part of basketball. Players learn to compete safely and respectfully." />
            <FAQCard question="What if my child isn't ready?" answer="That's why we exist! STAY ON bridges the gap in a safe, supervised environment." />
            <FAQCard question="Will this help team play?" answer="Absolutely. Pickup develops basketball IQ and competitive confidence." />
          </div>
        </div>
      </section>

      {/* SESSION PHOTOS GALLERY */}
      {sessionPhotos.length > 0 && (
        <section className="py-20 md:py-32 px-6 bg-card/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Game Day Moments</p>
              <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-4">
                Session Photos
              </h2>
              <p className="text-muted-foreground">Click a session to view all photos</p>
            </div>
            
            {/* Session Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {sessionPhotos.map((session) => {
                // Format date
                const formatDate = (dateStr) => {
                  if (!dateStr) return '';
                  const date = new Date(dateStr + 'T00:00:00');
                  return date.toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric'
                  });
                };
                
                const locationName = session.location?.name || 'Session';
                const coverPhoto = session.photos[0];
                const photoCount = session.photos.length;
                
                return (
                  <div 
                    key={session.session_id} 
                    className="group cursor-pointer"
                    onClick={() => setSelectedPhoto({ ...coverPhoto, sessionPhotos: session.photos, sessionInfo: session })}
                    data-testid={`session-card-${session.session_id}`}
                  >
                    {/* Cover Photo */}
                    <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3">
                      <img
                        src={coverPhoto?.secure_url ? coverPhoto.secure_url.replace('/upload/', '/upload/c_fill,w_600,h_450,q_auto,f_auto/') : coverPhoto?.url}
                        alt={`${locationName} session`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                      
                      {/* Photo Count Badge */}
                      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">
                        <span className="text-white text-sm font-medium">{photoCount} photos</span>
                      </div>
                      
                      {/* View More Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="bg-primary px-4 py-2 rounded-full">
                          <span className="text-white font-bold text-sm">View All</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Session Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-primary font-bold">
                        <Calendar size={16} />
                        <span>{formatDate(session.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground text-sm">
                        <MapPin size={14} />
                        <span>{locationName}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Follow CTA */}
            <div className="text-center mt-10">
              <a 
                href="https://www.instagram.com/stayonhoops" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <InstagramLogo size={24} weight="fill" />
                <span>Follow us for more highlights</span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Photo Lightbox / Session Gallery Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 overflow-y-auto"
          onClick={() => setSelectedPhoto(null)}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="fixed top-4 right-4 z-50 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
          >
            <X size={24} />
          </button>
          
          {/* If session photos exist, show gallery */}
          {selectedPhoto.sessionPhotos ? (
            <div className="max-w-6xl mx-auto px-4 py-16" onClick={(e) => e.stopPropagation()}>
              {/* Session Header */}
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {selectedPhoto.sessionInfo?.location?.name || 'Session Photos'}
                </h3>
                <p className="text-gray-400">
                  {selectedPhoto.sessionInfo?.date && new Date(selectedPhoto.sessionInfo.date + 'T00:00:00').toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </p>
                <p className="text-gray-500 text-sm mt-1">
                  {selectedPhoto.sessionPhotos.length} photos
                </p>
              </div>
              
              {/* Photos Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {selectedPhoto.sessionPhotos.map((photo, index) => (
                  <div
                    key={photo.public_id || index}
                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => window.open(photo.secure_url || photo.url, '_blank')}
                  >
                    <img
                      src={photo.secure_url ? photo.secure_url.replace('/upload/', '/upload/c_fill,w_400,h_400,q_auto,f_auto/') : photo.url}
                      alt="Session photo"
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">View Full</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Single photo view */
            <div className="flex items-center justify-center min-h-screen p-4">
              <img
                src={selectedPhoto.secure_url ? selectedPhoto.secure_url.replace('/upload/', '/upload/q_auto,f_auto/') : selectedPhoto.url}
                alt="Session photo"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      )}

      {/* CTA SECTION */}
      <section className="py-20 md:py-32 px-6 bg-card/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight mb-6">
            Ready To Ball?
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            Registration takes 2 minutes. Sessions fill up fast.
          </p>
          
          <Button 
            onClick={() => navigate('/onboarding')}
            className="btn-primary text-xl px-14 py-7"
            data-testid="cta-register-btn"
          >
            Get Started
            <ArrowRight size={24} weight="bold" className="ml-3" />
          </Button>
          
          <p className="text-muted-foreground text-sm mt-8">
            Questions? Email us at{' '}
            <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">
              info@stayonhoops.com
            </a>
          </p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-card border-t border-border py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
              alt="STAY ON Logo" 
              className="h-12 mb-6"
            />
            
            <a 
              href="https://www.instagram.com/stayonhoops" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors mb-6"
              data-testid="instagram-link"
            >
              <InstagramLogo size={32} weight="fill" />
            </a>
            
            <div className="flex flex-wrap justify-center gap-6 mb-6 text-sm">
              <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy</Link>
              <a href="mailto:info@stayonhoops.com" className="text-muted-foreground hover:text-primary transition-colors">Contact</a>
            </div>
            
            <p className="text-muted-foreground text-sm">
              © {new Date().getFullYear()} STAY ON Hoops. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Component: Feature Card
function FeatureCard({ icon, title, description, className = '', large = false }) {
  return (
    <Card className={`bg-card border-border card-hover p-8 group ${className}`}>
      <CardContent className="p-0">
        <div className="icon-container mb-6 text-primary group-hover:bg-primary/20">
          {icon}
        </div>
        <h3 className={`font-heading mb-3 ${large ? 'text-2xl md:text-3xl' : 'text-xl'}`}>{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
}

// Component: Detail Row
function DetailRow({ icon, title, text }) {
  return (
    <div className="flex items-start gap-4 group">
      <div className="icon-container text-primary shrink-0 group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <div>
        <h4 className="font-heading text-lg mb-1">{title}</h4>
        <p className="text-muted-foreground">{text}</p>
      </div>
    </div>
  );
}

// Component: Step Card
function StepCard({ number, title, description }) {
  return (
    <div className="text-center group">
      <div className="w-16 h-16 bg-primary/10 border border-primary/30 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 group-hover:border-primary/50 transition-all">
        <span className="text-2xl font-black text-primary">{number}</span>
      </div>
      <h4 className="font-heading text-xl mb-2">{title}</h4>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
}

// Component: FAQ Card
function FAQCard({ question, answer }) {
  return (
    <Card className="bg-card border-border p-6 card-hover">
      <CardContent className="p-0">
        <h4 className="font-heading text-base mb-3 text-primary">{question}</h4>
        <p className="text-muted-foreground text-sm leading-relaxed">{answer}</p>
      </CardContent>
    </Card>
  );
}
